import { useEffect, useRef, useState } from 'react';

// Tiny rAF tween for the Tower's live numbers (river ribbons, instrument values):
// every WS push moves the target; the drawn value eases there in ~600 ms so the
// river visibly DRAINS instead of jumping. No library. Honors
// prefers-reduced-motion (snaps). Keys that disappear snap to 0 next frame.

function reduceMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Duration scales with the size of the jump (relative to `target.total` when
 * present): a one-group tick eases in 600 ms, a whole gate filling in one push
 * (e.g. 302 tickets issued by a fast airline in 0.2 s) takes ~2.8 s to fill so the
 * eye reads the change as progress, not as a cut. Numbers are never invented — only
 * the easing is longer.
 */
export function useTweenMap(target: Record<string, number>, ms = 600): Record<string, number> {
  const [current, setCurrent] = useState<Record<string, number>>(target);
  const fromRef = useRef<Record<string, number>>(target);
  const rafRef = useRef<number | null>(null);
  const key = JSON.stringify(target);

  useEffect(() => {
    if (reduceMotion()) {
      fromRef.current = target;
      setCurrent(target);
      return;
    }
    const from = { ...fromRef.current };
    const t0 = performance.now();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const scale = Math.max(1, target.total ?? Math.max(...Object.values(target), 1));
    let maxDelta = 0;
    for (const k of Object.keys(target)) {
      if (k === 'total') continue;
      maxDelta = Math.max(maxDelta, Math.abs((target[k] ?? 0) - (from[k] ?? 0)));
    }
    const dur = Math.min(2800, Math.max(ms, ms + 2200 * Math.min(1, maxDelta / scale)));
    const step = (ts: number): void => {
      const t = Math.min(1, (ts - t0) / dur);
      const e = 1 - Math.pow(1 - t, 3);
      const next: Record<string, number> = {};
      for (const k of Object.keys(target)) {
        const a = from[k] ?? 0;
        const b = target[k] ?? 0;
        next[k] = a + (b - a) * e;
      }
      fromRef.current = next;
      setCurrent(next);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else rafRef.current = null;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // The serialized target is the dependency — a new object with the same
    // numbers must not restart the tween.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms]);

  return current;
}
